import type {
  ActiveUser,
  ActiveUsersResponse,
  UsageMetrics,
} from "../../types/usage";
import { baseFetch } from "./fetch";

interface OverviewData {
  statistics: {
    totalRuns: number;
    successfulRuns: number;
    completionRate: number;
    totalErrors: number;
  };
  recentActivity: Array<{
    date: string;
    count: number;
    errors: number;
  }>;
}

interface TypeScriptError {
  msg: string;
  severity: "error" | "warning" | "info";
  loc: string; // "file.ts:line:column" format
}

interface CompileRun {
  compileId: string;
  timestamp: string;
  userId?: string;
  projectId?: string;
  honcVersion?: string;
  errorCount: number;
  errorsJson?: string;
  r2Prefix?: string;
  legacy: boolean;
}

interface CompileRunWithErrors extends CompileRun {
  errors: TypeScriptError[];
}

interface RunsResponse {
  runs: CompileRun[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const api = {
  // Overview data for the dashboard
  getOverview: async (): Promise<OverviewData> => {
    return baseFetch<OverviewData>("/api/overview");
  },

  // List compile runs with optional filters
  getCompileRuns: async (
    params: {
      page?: number;
      limit?: number;
      userId?: string;
      projectId?: string;
      hasErrors?: string;
    } = {},
  ): Promise<RunsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) {
      searchParams.set("page", params.page.toString());
    }
    if (params.limit) {
      searchParams.set("limit", params.limit.toString());
    }
    if (params.userId) {
      searchParams.set("userId", params.userId);
    }
    if (params.projectId) {
      searchParams.set("projectId", params.projectId);
    }
    if (params.hasErrors) {
      searchParams.set("hasErrors", params.hasErrors);
    }

    const url = `/api/compile-runs${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return baseFetch<RunsResponse>(url);
  },

  // Get detailed information about a specific compile run
  getCompileRun: async (compileId: string): Promise<CompileRunWithErrors> => {
    return baseFetch<CompileRunWithErrors>(`/api/compile-runs/${compileId}`);
  },

  // Usage Analytics endpoints
  getCurrentUsage: async (): Promise<UsageMetrics> => {
    return baseFetch<UsageMetrics>("/api/usage/current");
  },

  getActiveUsers: async (): Promise<ActiveUsersResponse> => {
    return baseFetch<ActiveUsersResponse>("/api/usage/active-users");
  },

  getUsageTrends: async (): Promise<UsageMetrics[]> => {
    return baseFetch<UsageMetrics[]>("/api/usage/trends");
  },
};
